import { useQuery } from "@tanstack/react-query";
import { erp } from "@/lib/erp";
import type { StockReconciliation } from "@/lib/erp";
import type { StockReconciliationListParams } from "@/lib/erp/stockReconciliations";
import { STOCK_RECONCILIATION_LIST_FIELDS } from "@/lib/erp/stockReconciliations";
import type { FilterDescriptor } from "@/lib/erp";
import type { FrappeFilterArray } from "@/lib/erp/filterMapper";
import { useDocTypeListWithPagination } from "@/features/common/api/useDocTypeListWithPagination";

/**
 * Parameters for useStockReconciliationListWithPagination
 *
 * Note: filterDescriptors is the preferred way to pass filters.
 * The preMappedFilters param is for legacy/advanced use only (pre-mapped Frappe filters).
 */
export type StockReconciliationListHookParams = Omit<StockReconciliationListParams, "filters"> & {
  /**
   * FilterDescriptor[] to be converted to Frappe format internally.
   * This is the preferred way to pass filters for normal stock reconciliation use.
   */
  filterDescriptors?: FilterDescriptor[];
  /**
   * Legacy/advanced: pass pre-mapped Frappe filter array.
   * Prefer filterDescriptors in new code.
   *
   * @deprecated Use filterDescriptors instead. This is kept for backwards compatibility.
   */
  preMappedFilters?: FrappeFilterArray;
  /**
   * @deprecated Use preMappedFilters instead for clarity. Kept for backwards compatibility.
   */
  filters?: FrappeFilterArray;
  /**
   * Optional: fields to fetch from backend.
   * If not provided, defaults to STOCK_RECONCILIATION_LIST_FIELDS.
   * If provided, overrides the default fields list.
   */
  fields?: string[];
  /**
   * Search query string (will be converted to or_filters internally)
   */
  searchQuery?: string;
};

/**
 * Hook for fetching stock reconciliations with server-side pagination and filtering.
 *
 * This is a thin wrapper around the generic useDocTypeListWithPagination hook,
 * providing Stock Reconciliation–specific defaults (doctype: "Stock Reconciliation",
 * searchFields: ["name"]).
 *
 * IMPORTANT: This hook uses the generic docTypeListWithPagination path, which means:
 * - It does NOT automatically inject any special filters.
 * - If you need special filtering logic later, consider using
 *   erp.stockReconciliations.listWithPagination directly (requires mapping
 *   filterDescriptors to Frappe format first), or adding filters via
 *   filterDescriptors.
 */
export function useStockReconciliationListWithPagination(
  params: StockReconciliationListHookParams
) {
  // Support both preMappedFilters (preferred) and filters (deprecated) for backwards compatibility
  // Destructure fields separately to prevent it from being overwritten by spread operator
  const { preMappedFilters, filters, fields, ...restParams } = params;
  const legacyFilters = preMappedFilters || filters;

  return useDocTypeListWithPagination<StockReconciliation>({
    doctype: "Stock Reconciliation",
    searchFields: ["name"],
    fields: fields ?? [...STOCK_RECONCILIATION_LIST_FIELDS], // Use destructured fields or default
    listMethodOverride: "zaviago_backend.api.doc_list.list_with_system_fields", // Use POST method to avoid URL encoding issues with LIKE patterns
    ...restParams, // Now excludes fields, so it won't overwrite
    // Map to the generic hook's parameter name
    filters: legacyFilters,
  });
}

export function useStockReconciliation(name: string) {
  return useQuery({
    queryKey: ["stock-reconciliations", "detail", name],
    queryFn: () => erp.stockReconciliations.detail(name),
    enabled: !!name,
  });
}

